import { usePriceVisibility } from "@/composables/user/pricesVisibility";
import { useUser } from "@/composables/user/user";
import { useUserSettings } from "@/composables/user/userSettings";
import { migrateTokenFromIndexedDb, TokenService } from "@/services/user/auth/login/tokenService";
import { fetchUserSettings } from "@/services/user/settings/userSettingsApi";
import { type User } from "@/services/user/userTypes";
import PostHog from "@/services/analytics/postHog";
import { Authentication, type AuthenticationPromise } from "@/services/user/auth/login/authenticationService";
import { useAppStore } from "@/composables/appStore";
import { afterLogout } from "@/services/user/auth/login/afterLogoutService";
import { FallbackCurrencyId } from "@/services/currencies/currenciesTypes";

export async function checkUserOnInitialPageLoad(): Promise<AuthenticationPromise> {
  if (Authentication.isInProgressOrDone()) {
    return Authentication.promise;
  } else {
    await migrateTokenFromIndexedDb();
    const token = TokenService.get();
    if (token) {
      return Authentication.run({ token });
    } else {
      return null;
    }
  }
}

export async function login(email: string, password: string): Promise<AuthenticationPromise> {
  Authentication.reset();
  return Authentication.run({ email, password });
}

export async function logout(invalidateInApi = true): Promise<void> {
  await afterLogout(invalidateInApi);
  Authentication.reset();
}

export async function setupUserInApp(user: User): Promise<void> {
  TokenService.store(user.token);

  PostHog.identifyUser(user);

  const userStore = useUser();
  userStore.user.value = user;

  usePriceVisibility().arePricesVisible.value = user.showPrices;

  const userSettingsStore = useUserSettings();

  const { collector, bankInfo, address, isEmailVerified } = await fetchUserSettings();
  userSettingsStore.userSettings.value = collector;
  userSettingsStore.bankInfo.value = bankInfo;
  userSettingsStore.address.value = address;
  userSettingsStore.isEmailVerified.value = isEmailVerified;

  const appStore = useAppStore();
  const userPreferredCurrencyId = userSettingsStore.userSettings.value?.displayCurrencyId ?? FallbackCurrencyId;
  appStore.setCurrencyId(userPreferredCurrencyId);
}
